/**
 * Cloudflare Turnstile — the ONE place the widget script/API is loaded and
 * typed for the Email-OTP send flow (ato-api#264 close condition 2). The
 * server guard (`email_otp_guard.ts`) rejects any send whose Turnstile token
 * doesn't verify against Cloudflare's siteverify with the EXACT action
 * `email_otp_send` and the PWA's own origin as the hostname — so the widget
 * MUST render with that action, and a token must be re-fetched (the widget
 * reset) for every send because a Turnstile token is single-use.
 *
 * Loaded explicitly (`?render=explicit`) rather than auto-rendered so the
 * caller owns the widget lifecycle (render once, `reset()` for a fresh token
 * per send, `remove()` on unmount) instead of Turnstile scanning the DOM.
 */

/** The `action` Cloudflare siteverify must see — MUST match the server guard's
 *  `TURNSTILE_EMAIL_OTP_SEND_ACTION` (`email_otp_guard.ts`) exactly. */
export const TURNSTILE_ACTION_EMAIL_OTP_SEND = "email_otp_send";

export interface TurnstileRenderOptions {
  sitekey: string;
  action?: string;
  callback?: (token: string) => void;
  "error-callback"?: () => void;
  "expired-callback"?: () => void;
  "timeout-callback"?: () => void;
  theme?: "light" | "dark" | "auto";
  size?: "normal" | "flexible" | "compact";
  appearance?: "always" | "execute" | "interaction-only";
}

export interface TurnstileApi {
  render(container: HTMLElement, options: TurnstileRenderOptions): string;
  reset(widgetId?: string): void;
  remove(widgetId?: string): void;
  getResponse(widgetId?: string): string | undefined;
}

// The ONE `window.turnstile` declaration in this app. Every Turnstile surface
// (this Email-OTP widget, the beta-waitlist modal, the public-post report
// modal) shares it rather than declaring its own competing shape.
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/** Resolve the site key fresh (never captured at import time, so a test can
 *  stub `import.meta.env` and a build-time value is always honored). Empty
 *  when unconfigured — callers treat that as "email sign-in unavailable"
 *  rather than sending a request the server would reject with 403. */
export function turnstileSiteKey(): string {
  return (import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "").trim();
}

export function isTurnstileConfigured(): boolean {
  return turnstileSiteKey().length > 0;
}

let scriptPromise: Promise<void> | null = null;

/**
 * Inject the Turnstile script exactly once (idempotent across every widget
 * mount and across a failed-then-retried load). Resolves immediately when the
 * API is already present (the common case after the first mount, and how tests
 * inject a stubbed `window.turnstile`). Rejects — and clears the cached
 * promise so a later mount can retry — if the script fails to load.
 */
export function loadTurnstileScript(): Promise<void> {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.resolve();
  }
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const onReady = () => resolve();
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src^="https://challenges.cloudflare.com/turnstile/"]`,
    );
    if (existing) {
      if (window.turnstile) {
        resolve();
      } else {
        existing.addEventListener("load", onReady, { once: true });
        existing.addEventListener(
          "error",
          () => {
            scriptPromise = null;
            reject(new Error("turnstile_script_failed"));
          },
          { once: true },
        );
      }
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", onReady, { once: true });
    script.addEventListener(
      "error",
      () => {
        scriptPromise = null;
        reject(new Error("turnstile_script_failed"));
      },
      { once: true },
    );
    document.head.appendChild(script);
  });
  return scriptPromise;
}
